import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function ZoningCompliancePage() {
  const zoningDistricts = [
    {
      title: 'Commercial Zones',
      code: 'C-1, C-2, C-3',
      icon: 'ri-store-2-line',
      description: 'Retail stores, restaurants, offices, and service businesses',
      allowedUses: [
        'Retail establishments and shopping centers',
        'Restaurants and food service businesses',
        'Professional offices and medical clinics',
        'Personal services (salons, dry cleaners, etc.)',
        'Banks and financial institutions',
        'Hotels and lodging facilities',
      ],
      restrictions: [
        'Building height limits vary by zone (typically 35-75 feet)',
        'Parking requirements based on square footage',
        'Setback requirements from property lines',
        'Signage size and placement restrictions',
      ],
    },
    {
      title: 'Industrial Zones',
      code: 'I-1, I-2',
      icon: 'ri-building-line',
      description: 'Manufacturing, warehousing, and distribution facilities',
      allowedUses: [
        'Light and heavy manufacturing operations',
        'Warehousing and distribution centers',
        'Research and development facilities',
        'Wholesale trade establishments',
        'Truck terminals and freight facilities',
        'Industrial services and repair shops',
      ],
      restrictions: [
        'Environmental impact assessments may be required',
        'Noise and emission regulations apply',
        'Loading dock and truck access requirements',
        'Hazardous materials storage regulations',
      ],
    },
    {
      title: 'Mixed-Use Zones',
      code: 'MU-1, MU-2',
      icon: 'ri-community-line',
      description: 'Combination of residential, commercial, and office uses',
      allowedUses: [
        'Ground floor retail with upper floor residential',
        'Office spaces combined with retail',
        'Restaurants and entertainment venues',
        'Live-work units and artist studios',
        'Community facilities and services',
        'Urban residential developments',
      ],
      restrictions: [
        'Specific use percentages required (e.g., 60% residential)',
        'Design standards for building facades',
        'Pedestrian-oriented development requirements',
        'Parking shared between uses',
      ],
    },
    {
      title: 'Home-Based Business',
      code: 'Residential Zones',
      icon: 'ri-home-office-line',
      description: 'Businesses operated from residential properties',
      allowedUses: [
        'Professional services (consulting, accounting)',
        'Online retail and e-commerce',
        'Creative services (design, writing)',
        'Small-scale crafts and artisan work',
        'Tutoring and instruction (limited students)',
        'Home offices for remote work',
      ],
      restrictions: [
        'No exterior signage or commercial appearance',
        'Limited client/customer visits',
        'No employees other than residents',
        'No inventory storage visible from outside',
        'Parking must not impact neighborhood',
        'Quiet hours must be observed',
      ],
    },
  ];

  const verificationSteps = [
    {
      step: '1',
      title: 'Find Your Property Address',
      description: 'Locate the exact street address and legal description of your business location.',
    },
    {
      step: '2',
      title: 'Check Zoning Map',
      description: 'Visit the City of El Paso Planning Department website or office to view the official zoning map.',
    },
    {
      step: '3',
      title: 'Review Zoning Code',
      description: 'Read the specific regulations for your zoning district in the El Paso Municipal Code.',
    },
    {
      step: '4',
      title: 'Verify Permitted Uses',
      description: 'Confirm your business type is listed as a permitted or conditional use in that zone.',
    },
    {
      step: '5',
      title: 'Contact Planning Department',
      description: 'Speak with a planner to clarify any questions and confirm your business is compliant.',
    },
  ];

  const buildingCodes = [
    {
      category: 'Building Permits',
      icon: 'ri-file-list-3-line',
      requirements: [
        'New construction requires full building permit',
        'Renovations over $5,000 require permit',
        'Change of occupancy classification requires permit',
        'Structural modifications always require permit',
        'Electrical, plumbing, and HVAC work requires separate permits',
      ],
    },
    {
      category: 'Fire Safety',
      icon: 'ri-fire-line',
      requirements: [
        'Fire sprinkler systems for buildings over 5,000 sq ft',
        'Fire alarm systems based on occupancy type',
        'Emergency exit signage and lighting',
        'Fire extinguishers properly placed and maintained',
        'Annual fire safety inspections required',
      ],
    },
    {
      category: 'Accessibility (ADA)',
      icon: 'ri-wheelchair-line',
      requirements: [
        'Accessible parking spaces (minimum 1 per 25 spaces)',
        'Wheelchair ramps with proper slope (1:12 ratio)',
        'Accessible restrooms with grab bars',
        'Doorways minimum 32 inches clear width',
        'Accessible routes throughout facility',
      ],
    },
    {
      category: 'Energy Efficiency',
      icon: 'ri-leaf-line',
      requirements: [
        'Compliance with International Energy Conservation Code',
        'Insulation requirements for walls and roofs',
        'Energy-efficient HVAC systems',
        'LED or energy-efficient lighting',
        'Energy audit may be required for large buildings',
      ],
    },
  ];

  const signageRegulations = [
    {
      type: 'Wall Signs',
      maxSize: '10% of building facade',
      restrictions: ['Cannot project more than 12 inches', 'Must be professionally installed', 'Requires electrical permit if illuminated'],
    },
    {
      type: 'Monument Signs',
      maxSize: '32 sq ft per face',
      restrictions: ['Maximum height 6 feet', 'Setback 10 feet from property line', 'Landscaping required around base'],
    },
    {
      type: 'Window Signs',
      maxSize: '25% of window area',
      restrictions: ['Temporary signs limited to 30 days', 'No flashing or animated signs', 'Must not obstruct visibility'],
    },
    {
      type: 'Temporary Signs',
      maxSize: '6 sq ft',
      restrictions: ['Maximum 2 signs per property', 'Limited to 90 days per year', 'No signs in public right-of-way'],
    },
  ];

  const environmentalRegs = [
    {
      title: 'Air Quality Permits',
      applies: 'Manufacturing, auto repair, painting operations',
      requirements: ['Texas Commission on Environmental Quality (TCEQ) permit', 'Emissions monitoring and reporting', 'Pollution control equipment'],
    },
    {
      title: 'Water Discharge Permits',
      applies: 'Industrial facilities, car washes, food processing',
      requirements: ['Wastewater discharge permit from El Paso Water', 'Pretreatment of industrial wastewater', 'Regular water quality testing'],
    },
    {
      title: 'Hazardous Materials',
      applies: 'Chemical storage, laboratories, manufacturing',
      requirements: ['Hazardous Materials Business Plan', 'Proper storage and labeling', 'Emergency response procedures', 'Annual reporting to fire department'],
    },
    {
      title: 'Waste Management',
      applies: 'All businesses generating waste',
      requirements: ['Proper waste disposal contracts', 'Recycling program (for large businesses)', 'Special handling for hazardous waste', 'Medical waste permits (healthcare)'],
    },
  ];

  const varianceProcess = [
    {
      step: 'Submit Application',
      description: 'Complete variance application with detailed justification and site plans',
      timeline: 'Week 1',
    },
    {
      step: 'Staff Review',
      description: 'Planning Department reviews application for completeness and compliance',
      timeline: 'Weeks 2-3',
    },
    {
      step: 'Public Notice',
      description: 'Property owners within 200 feet are notified of variance request',
      timeline: 'Week 4',
    },
    {
      step: 'Board Hearing',
      description: 'Present your case to the Board of Adjustment at public hearing',
      timeline: 'Weeks 5-6',
    },
    {
      step: 'Decision',
      description: 'Board votes to approve, approve with conditions, or deny variance',
      timeline: 'Week 6',
    },
    {
      step: 'Appeal Period',
      description: '15-day period for appeals; if none, decision becomes final',
      timeline: 'Weeks 7-8',
    },
  ];

  const contacts = [
    {
      department: 'Planning & Inspections Department',
      address: '800 E Overland Ave, El Paso, TX 79901',
      phone: '(915) 212-1553',
      email: 'planning@elpasotexas.gov',
      services: ['Zoning information', 'Building permits', 'Code compliance'],
    },
    {
      department: 'Development Services',
      address: '800 E Overland Ave, El Paso, TX 79901',
      phone: '(915) 212-1556',
      email: 'devservices@elpasotexas.gov',
      services: ['Site plan review', 'Subdivision approval', 'Development agreements'],
    },
    {
      department: 'Fire Prevention Bureau',
      address: '11 Civic Center Plaza, El Paso, TX 79901',
      phone: '(915) 212-5615',
      email: 'fireprevention@elpasotexas.gov',
      services: ['Fire inspections', 'Fire safety permits', 'Hazardous materials'],
    },
    {
      department: 'Environmental Services',
      address: '6006 N Desert Blvd, El Paso, TX 79912',
      phone: '(915) 212-6000',
      email: 'environmental@elpasotexas.gov',
      services: ['Environmental permits', 'Waste management', 'Air quality'],
    },
  ];

  return (
    <>
      <SEOHead
        title="El Paso Zoning Compliance & Land Use Regulations Guide"
        description="Comprehensive guide to El Paso zoning compliance and land use regulations. Learn about commercial, industrial, and residential zoning, variance applications, and compliance requirements for El Paso businesses."
        canonical="/regulations/zoning-compliance"
        keywords="El Paso zoning, commercial zoning El Paso, land use regulations Texas, zoning compliance, El Paso building codes, variance application"
        ogImage="https://public.readdy.ai/ai/img_res/422a0379cd0b60e2d66d754f210ad086.jpg"
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <main className="pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-8">
              <Link to="/regulations" className="text-[#D2691E] hover:underline text-sm font-medium">
                ← Back to Regulations
              </Link>
            </nav>

            {/* Breadcrumb Structured Data */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://elpasotexasbusiness.com/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Regulations",
                    "item": "https://elpasotexasbusiness.com/regulations"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Zoning Compliance",
                    "item": "https://elpasotexasbusiness.com/regulations/zoning-compliance"
                  }
                ]
              })}
            </script>

            {/* Header */}
            <div className="mb-16">
              <h1 className="text-5xl font-bold text-gray-900 mb-6">Zoning & Compliance</h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                Navigate El Paso's zoning regulations and building codes to ensure your business location and operations are fully compliant with local requirements.
              </p>
            </div>

            {/* Zoning Districts */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">El Paso Zoning Districts</h2>
              <p className="text-sm text-gray-600 mb-10">Understanding zoning classifications and what's allowed in each district.</p>
              
              <div className="space-y-8">
                {zoningDistricts.map((zone, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${zone.icon} text-2xl text-white`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-2xl font-bold text-gray-900">{zone.title}</h3>
                          <span className="px-3 py-1 bg-[#D2691E]/10 text-[#D2691E] text-xs font-semibold rounded-full">
                            {zone.code}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{zone.description}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 ml-18">
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-4">Allowed Uses</h4>
                        <ul className="space-y-3">
                          {zone.allowedUses.map((use, useIndex) => (
                            <li key={useIndex} className="flex items-start space-x-3">
                              <div className="w-6 h-6 bg-[#E8F5E9] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <i className="ri-check-line text-green-600 text-sm"></i>
                              </div>
                              <span className="text-sm text-gray-700 leading-relaxed">{use}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900 mb-4">Key Restrictions</h4>
                        <ul className="space-y-3">
                          {zone.restrictions.map((restriction, resIndex) => (
                            <li key={resIndex} className="flex items-start space-x-3">
                              <div className="w-6 h-6 bg-[#FFF3E0] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <i className="ri-alert-line text-orange-600 text-sm"></i>
                              </div>
                              <span className="text-sm text-gray-700 leading-relaxed">{restriction}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Verification Steps */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">How to Verify Zoning for Your Location</h2>
              <p className="text-sm text-gray-600 mb-10">Follow these steps to confirm your business is in the right zone.</p>
              
              <div className="bg-[#FAFAF8] rounded-2xl p-8">
                <div className="space-y-6">
                  {verificationSteps.map((item, index) => (
                    <div key={index} className="flex items-start space-x-6">
                      <div className="w-12 h-12 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-xl font-bold text-white">{item.step}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Building Code Compliance */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Building Code Compliance</h2>
              <p className="text-sm text-gray-600 mb-10">Essential building code requirements for El Paso businesses.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {buildingCodes.map((code, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="w-12 h-12 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${code.icon} text-xl text-white`}></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">{code.category}</h3>
                    </div>
                    <ul className="space-y-3">
                      {code.requirements.map((req, reqIndex) => (
                        <li key={reqIndex} className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-[#E8F5E9] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-green-600 text-sm"></i>
                          </div>
                          <span className="text-sm text-gray-700 leading-relaxed">{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Signage Regulations */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Signage Permit Requirements</h2>
              <p className="text-sm text-gray-600 mb-10">Regulations for business signs in El Paso.</p>
              
              <div className="bg-[#FAFAF8] rounded-2xl p-8">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-4 px-4 text-sm font-bold text-gray-900">Sign Type</th>
                        <th className="text-left py-4 px-4 text-sm font-bold text-gray-900">Maximum Size</th>
                        <th className="text-left py-4 px-4 text-sm font-bold text-gray-900">Key Restrictions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {signageRegulations.map((sign, index) => (
                        <tr key={index} className="border-b border-gray-100">
                          <td className="py-4 px-4 text-sm font-semibold text-gray-900">{sign.type}</td>
                          <td className="py-4 px-4 text-sm text-gray-700">{sign.maxSize}</td>
                          <td className="py-4 px-4">
                            <ul className="space-y-2">
                              {sign.restrictions.map((restriction, resIndex) => (
                                <li key={resIndex} className="text-sm text-gray-700 flex items-start space-x-2">
                                  <span className="text-[#D2691E] mt-1">•</span>
                                  <span>{restriction}</span>
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Environmental Regulations */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Environmental Regulations</h2>
              <p className="text-sm text-gray-600 mb-10">Environmental compliance requirements for specific business types.</p>
              
              <div className="space-y-6">
                {environmentalRegs.map((reg, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{reg.title}</h3>
                        <p className="text-sm text-gray-600">
                          <strong>Applies to:</strong> {reg.applies}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {reg.requirements.map((req, reqIndex) => (
                        <li key={reqIndex} className="flex items-start space-x-3">
                          <div className="w-6 h-6 bg-[#E8F5E9] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-green-600 text-sm"></i>
                          </div>
                          <span className="text-sm text-gray-700 leading-relaxed">{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Variance Process */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Applying for a Zoning Variance</h2>
              <p className="text-sm text-gray-600 mb-10">If your business doesn't fit standard zoning, you may request a variance.</p>
              
              <div className="bg-[#FAFAF8] rounded-2xl p-8">
                <div className="space-y-6">
                  {varianceProcess.map((item, index) => (
                    <div key={index} className="flex items-start space-x-6">
                      <div className="flex-shrink-0">
                        <div className="w-20 h-20 bg-white rounded-xl flex flex-col items-center justify-center border-2 border-[#D2691E]">
                          <span className="text-xs font-semibold text-[#D2691E] uppercase">{item.timeline}</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.step}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 p-6 bg-[#FFF3E0] rounded-xl">
                  <div className="flex items-start space-x-3">
                    <i className="ri-information-line text-orange-600 text-xl mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-2">Important Notes:</p>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• Variance application fee: $500-$1,000 depending on request type</li>
                        <li>• Must demonstrate hardship or unique property circumstances</li>
                        <li>• Variance runs with the property, not the owner</li>
                        <li>• Approval is not guaranteed; prepare a strong justification</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Contacts */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Important Contacts</h2>
              <p className="text-sm text-gray-600 mb-10">Connect with these departments for zoning and compliance assistance.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {contacts.map((contact, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">{contact.department}</h3>
                    <div className="space-y-3 mb-4">
                      <div className="flex items-start space-x-3">
                        <i className="ri-map-pin-line text-[#D2691E] mt-1"></i>
                        <span className="text-sm text-gray-700">{contact.address}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-phone-line text-[#D2691E]"></i>
                        <span className="text-sm text-gray-700">{contact.phone}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <i className="ri-mail-line text-[#D2691E]"></i>
                        <span className="text-sm text-gray-700">{contact.email}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-900 mb-2">Services:</p>
                      <div className="flex flex-wrap gap-2">
                        {contact.services.map((service, serviceIndex) => (
                          <span key={serviceIndex} className="px-3 py-1 bg-white text-gray-700 text-xs rounded-full">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-[#D2691E] to-[#A0522D] rounded-2xl p-12 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">Questions About Zoning or Compliance?</h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
                Our team can help you navigate zoning regulations and ensure your business location meets all compliance requirements.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-white text-[#D2691E] px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Get Help Today
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}